
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ProfileData } from "@/types/profile";
import { motion } from "framer-motion";

// Import refactored components
import ProfileHeader from "./dashboard/ProfileHeader";
import ProfileBio from "./dashboard/ProfileBio";
import ProfileStats from "./dashboard/ProfileStats";
import { useProfileStats } from "./dashboard/useProfileStats";
import { useAdminStatus } from "@/hooks/useAdminStatus";

interface ProfileDashboardProps {
  profile: ProfileData;
}

const ProfileDashboard: React.FC<ProfileDashboardProps> = ({ profile }) => {
  // Get profile stats using the custom hook
  const { followersCount, followingCount, loading } = useProfileStats();
  const { isAdmin } = useAdminStatus(profile?.id);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1, 
      transition: { type: "spring", stiffness: 100 }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Card className="w-full shadow-sm transition-all duration-300 hover:shadow-md">
        <motion.div variants={itemVariants}>
          <CardHeader className="pb-3">
            <CardTitle className="text-xl font-medium">Profile Dashboard</CardTitle>
          </CardHeader>
        </motion.div>
        
        <CardContent className="space-y-6">
          {/* Profile header with avatar and name */}
          <ProfileHeader 
            fullName={profile.full_name || ''}
            username={profile.username || ''}
            avatarUrl={profile.avatar_url}
            country={profile.country}
            state={profile.state}
            school={profile.school}
            gradeLevel={profile.grade_level}
          />
          
          <motion.div variants={itemVariants}>
            <Separator />
          </motion.div>
          
          {/* Bio and interests */}
          <ProfileBio 
            bio={profile.bio || ''}
            interests={profile.interests || ''}
          />
          
          <motion.div variants={itemVariants}>
            <Separator />
          </motion.div>
          
          {/* Stats: Karma, followers, following */}
          <ProfileStats 
            karmaPoints={profile.karma_points || 0}
            karmaLevel={profile.karma_level || 1}
            followersCount={followersCount}
            followingCount={followingCount}
            loading={loading}
            isAdmin={isAdmin}
          />
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ProfileDashboard;
